import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { dashboardAPI } from '../services/api'
import { isSessionExpired, SESSION_EXPIRED_SENTINEL } from '../services/api'

// ---------------------------------------------------------------------------
// Types — match the backend /dashboard/stats response shape
// ---------------------------------------------------------------------------

export interface DashboardStats {
  summary: {
    availableDeals: number
    unlockedDeals: number
    savedDeals: number
    creditsRemaining: number
    tier: string
  }
  marketOverview: {
    marketSnapshot: {
      totalProperties: number
      totalDeals: number
      avgScore: number
      priceRange: { min: number; max: number }
      topStrategy: string
      newThisWeek: number
      strategyDistribution: Record<string, number>
    }
    dealFreshness: string | null
    topOpportunities: Array<{
      id: string
      propertyId: string
      price: number
      city: string | null
      postcodeSector: string
      propertyType: string
      bedrooms: number
      totalScore: number | null
      qualityRating: string | null
      primaryStrategy: string | null
      estimatedYield: number | null
      imageUrl: string | null
    }>
  }
  intelligence: {
    concentrationClusters: Array<{
      district: string
      coords: [number, number] | null
      totalDeals: number
      dominantStrategy: string
      avgScore: number
      strategies: Record<string, { count: number; avgScore: number }>
    }>
    strategyHotspots: Array<{
      district: string
      city: string
      strategy: string
      dealCount: number
      avgScore: number
      avgMetric: number
      rankScore: number
    }>
    locationReturns: Array<{
      city: string
      dealCount: number
      avgMetric: number
      avgScore: number
    }>
    strategyHeatmap: {
      cities: string[]
      strategies: string[]
      cells: Array<{
        city: string
        strategy: string
        avgScore: number
        dealCount: number
      }>
    }
  }
  portfolioInsights: {
    portfolioSummary: {
      totalUnlocked: number
      byStrategy: Record<string, number>
    }
    portfolioGaps: Array<{
      type: string
      headline: string
      description: string
      unexploredCount: number
      avgScore: number
      strategy?: string
      relevance: number
    }>
    valueFinder: {
      points: Array<{
        price: number
        score: number
        strategy: string
        postcodeSector: string
        id: string
      }>
      topValues: Array<{
        id: string
        price: number
        score: number | null
        strategy: string | null
        postcodeSector: string
        city: string | null
      }>
      regression: { slope: number; intercept: number }
    }
  }
}

interface DashboardState {
  stats: DashboardStats | null
  loading: boolean
  error: string | null
  lastFetched: number | null
}

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

const initialState: DashboardState = {
  stats: null,
  loading: false,
  error: null,
  lastFetched: null,
}

// ---------------------------------------------------------------------------
// Thunk
// ---------------------------------------------------------------------------

export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchStats',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = (getState() as any).dashboard as DashboardState
      const now = Date.now()

      // Use cache if fresh
      if (state.stats && state.lastFetched && now - state.lastFetched < CACHE_TTL) {
        return { stats: state.stats, fromCache: true }
      }

      const stats = await dashboardAPI.getStats()
      return { stats, fromCache: false }
    } catch (err: any) {
      if (isSessionExpired(err)) return rejectWithValue(SESSION_EXPIRED_SENTINEL)
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to load dashboard')
    }
  }
)

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardStats: (state) => {
      state.stats = null
      state.lastFetched = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        if (!state.stats) state.loading = true // only show spinner on initial load
        state.error = null
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading = false
        state.stats = action.payload.stats
        if (!action.payload.fromCache) {
          state.lastFetched = Date.now()
        }
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading = false
        if (action.payload !== SESSION_EXPIRED_SENTINEL) {
          state.error = action.payload as string
        }
      })
  },
})

export const { clearDashboardStats } = dashboardSlice.actions
export default dashboardSlice.reducer

// Selectors
export const selectDashboardStats = (state: any) => state.dashboard?.stats as DashboardStats | null
export const selectDashboardLoading = (state: any) => state.dashboard?.loading as boolean
export const selectDashboardError = (state: any) => state.dashboard?.error as string | null
